import { X, RadioTower, Split, Boxes, Cable, Users, Ruler, GitFork, Activity, Share2, Rows3 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const ICONS = { olt: RadioTower, splitter: Split, closure: Boxes, cable: Cable, client: Users, odf: Rows3 };

export default function TracePanel({ trace, onClose, onSchematic }) {
  const { t } = useI18n();
  if (!trace) return null;
  const attColor = trace.attenuation_db > 28 ? "text-red-400" : trace.attenuation_db > 24 ? "text-yellow-400" : "text-green-400";

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="trace-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-gradient-to-r from-cyan-900/40 to-slate-900">
        <div className="flex items-center gap-2">
          <Activity size={18} className="text-cyan-400" />
          <h2 className="font-heading font-bold text-base">{t("Śledzenie sygnału")}</h2>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onSchematic} data-testid="trace-schematic" title={t("Schemat graficzny")}
            className="text-slate-300 hover:text-cyan-400 p-1"><Share2 size={17} /></button>
          <button onClick={onClose} data-testid="trace-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-slate-800">
        <div className="text-sm font-medium">{trace.client?.name}</div>
        <div className="text-xs text-slate-400">{trace.client?.address}</div>
      </div>

      <div className="grid grid-cols-3 gap-px bg-slate-800 border-b border-slate-700">
        <Stat icon={Ruler} label={t("Długość")} value={`${(trace.total_length_m / 1000).toFixed(2)} km`} />
        <Stat icon={GitFork} label={t("Spawy")} value={trace.splice_count} />
        <Stat icon={Activity} label={t("Tłumienie")} value={`${Number(trace.attenuation_db).toFixed(2)} dB`} valueClass={attColor} />
      </div>

      {(trace.dist_to_olt_m != null || trace.dist_to_splitter_m != null) && (
        <div className="px-4 py-3 border-b border-slate-800 space-y-2" data-testid="trace-distances">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{t("Odległości geograficzne")}</div>
          {trace.dist_to_olt_m != null && (
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-slate-300"><RadioTower size={14} className="text-blue-400" /> {t("Kabel do OLT")}</span>
              <span className="font-mono text-cyan-400">{(trace.dist_to_olt_m / 1000).toFixed(3)} km <span className="text-slate-500">({trace.dist_to_olt_m} m)</span></span>
            </div>
          )}
          {trace.dist_to_splitter_m != null && (
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-slate-300"><Split size={14} className="text-orange-400" /> {t("Do splittera")} {trace.nearest_splitter_name || ""}</span>
              <span className="font-mono text-cyan-400">{(trace.dist_to_splitter_m / 1000).toFixed(3)} km <span className="text-slate-500">({trace.dist_to_splitter_m} m)</span></span>
            </div>
          )}
        </div>
      )}

      {trace.optical_budget && (() => {
        const b = trace.optical_budget;
        const meta = {
          ok: { label: t("W normie"), cls: "bg-green-900/40 text-green-400 border-green-700" },
          ostrzezenie: { label: t("Ostrzeżenie"), cls: "bg-yellow-900/40 text-yellow-400 border-yellow-700" },
          krytyczny: { label: t("Przekroczony próg"), cls: "bg-red-900/40 text-red-400 border-red-700" },
          brak: { label: t("Brak pomiaru"), cls: "bg-slate-800 text-slate-400 border-slate-600" },
        }[b.status] || { label: b.status, cls: "bg-slate-800 text-slate-400 border-slate-600" };
        const row = (label, value, cls = "text-slate-200") => (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">{label}</span>
            <span className={`font-mono ${cls}`}>{value}</span>
          </div>
        );
        return (
          <div className="px-4 py-3 border-b border-slate-800 space-y-1.5" data-testid="trace-budget">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{t("Budżet optyczny @")} {b.wavelength} nm</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${meta.cls}`} data-testid="budget-status">{meta.label}</span>
            </div>
            {row(t("Moc nadawcza OLT"), `${b.tx_power_dbm.toFixed(2)} dBm`)}
            {row(t("Straty łącza"), `−${b.attenuation_db.toFixed(2)} dB`, "text-orange-300")}
            {row(t("Oczekiwana moc odbioru"), `${b.expected_rx_dbm.toFixed(2)} dBm`, "text-cyan-300")}
            {b.measured_rx_dbm != null
              ? row(t("Zmierzona moc odbioru"), `${b.measured_rx_dbm.toFixed(2)} dBm`, b.status === "krytyczny" ? "text-red-400" : b.status === "ostrzezenie" ? "text-yellow-400" : "text-green-400")
              : row(t("Zmierzona moc odbioru"), t("— brak"), "text-slate-500")}
            {b.deviation_db != null && row(t("Odchyłka (pomiar − oczekiwana)"), `${b.deviation_db > 0 ? "+" : ""}${b.deviation_db.toFixed(2)} dB`, b.deviation_db < 0 ? "text-red-300" : "text-green-400")}
            <div className="text-[11px] text-slate-500 pt-1">{t("Próg min. odbioru:")} {b.min_rx_dbm.toFixed(1)} dBm</div>
          </div>
        );
      })()}

      <div className="flex-1 overflow-y-auto p-4">
        <div className="relative pl-6">
          <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-cyan-400 to-blue-600" />
          {trace.steps.map((s, i) => {
            const Icon = ICONS[s.kind] || Boxes;
            return (
              <div key={i} className="relative mb-4" data-testid={`trace-step-${i}`}>
                <div className="absolute -left-6 top-0.5 w-[19px] h-[19px] rounded-full bg-slate-800 border-2 border-cyan-400 flex items-center justify-center">
                  <Icon size={10} className="text-cyan-400" />
                </div>
                <div className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-cyan-400 font-semibold">{t(s.label)}</span>
                    <span className="text-sm font-medium">{s.name}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5 font-mono">{s.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, valueClass = "text-white" }) {
  return (
    <div className="bg-slate-900 px-3 py-2.5 text-center">
      <Icon size={14} className="mx-auto text-slate-500 mb-1" />
      <div className={`font-mono text-sm font-semibold ${valueClass}`}>{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
